import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { jpegDataUrlToBlob } from '@shared/services/label-photo-upload';
import { resolveApiBaseUrl } from './api-base-url';
import {
  Basket,
  BasketComparison,
  BasketNeed,
  BasketNeedWrite,
  BranchOption,
  BranchesResponse,
  CitiesResponse,
  PriceHistoryResponse,
  ProductSearchItem,
  ProductSearchResponse,
  CatalogPricesResponse,
  ShoppingBasket,
  ProductAlternativeMatch,
  SimilarProductsResponse,
  SmartBasketComparison,
  SmartCompareRequest,
  UnitReviewSourceItem,
  UnitReviewStatus,
  ProductUnitReview,
  ProductUnitReviewListResponse,
  NamePreviewResult,
  DisplayNameWriteRequest,
  StorefrontSizeMismatchList,
  ClassificationCodesResponse,
  ClassificationPreview,
  ClassificationProposal,
  ClassificationProposalProduct,
  ClassificationAutosavedItem,
  ClassificationGraphRun,
  ClassificationModelsResponse,
  ClassificationModelInfo,
  ClassificationProbePrepare,
  ClassificationFlowSample,
  ClassificationProbeGoldSample,
  ClassificationProbeRun,
  ClassificationFlowEvent,
  ClassificationFlowBatchCallDetail,
  ClassificationFlowBatchDetail,
  ClassificationFlowBatchItemDetail,
  ClassificationFlowBatchSummary,
  ClassificationFlowVoteApproval,
  ClassificationVerifyRequest,
  ClassificationVerifyResult,
  CreateClassificationTypeRequest,
  TypeCode,
  WorkersStatusResponse,
  WorkersFeedResponse,
  WorkersIssuesResponse,
  WorkerSuggestedAction,
  SiteVisitListResponse
} from '@core/models/models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = resolveApiBaseUrl(
    globalThis.location?.hostname ?? 'localhost',
    environment.apiBaseUrl,
    globalThis.location?.protocol ?? 'http:'
  );

  listCities(): Observable<CitiesResponse> {
    return this.http.get<CitiesResponse>(`${this.baseUrl}/api/branches/cities`);
  }

  listPhysicalBranches(city: string): Observable<BranchesResponse> {
    const params = new HttpParams().set('city', city);
    return this.http.get<BranchesResponse>(`${this.baseUrl}/api/branches`, { params });
  }

  getBranch(branchId: number): Observable<BranchOption> {
    return this.http.get<BranchOption>(`${this.baseUrl}/api/branches/${branchId}`);
  }

  estimateBagWeight(
    productId: number,
    productName: string,
    frameJpeg: string
  ): Observable<{ photoId: number; kilograms: number | null; readable: boolean }> {
    let blob: Blob;
    try {
      blob = jpegDataUrlToBlob(frameJpeg);
    } catch {
      return EMPTY;
    }

    const body = new FormData();
    body.append('productId', String(productId));
    body.append('productName', productName);
    body.append('file', blob, 'bag.jpg');
    return this.http.post<{ photoId: number; kilograms: number | null; readable: boolean }>(
      `${this.baseUrl}/api/products/weight-photos`,
      body
    );
  }

  uploadLabelPhoto(barcode: string, frameJpeg: string): Observable<void> {
    let blob: Blob;
    try {
      blob = jpegDataUrlToBlob(frameJpeg);
    } catch {
      return EMPTY;
    }

    const body = new FormData();
    body.append('barcode', barcode);
    body.append('file', blob, 'label.jpg');
    return this.http
      .post(`${this.baseUrl}/api/products/label-photos`, body, {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(map(() => undefined));
  }

  searchProducts(query: string, city: string, limit = 20): Observable<ProductSearchResponse> {
    const params = new HttpParams()
      .set('q', query)
      .set('city', city)
      .set('limit', limit);
    return this.http.get<ProductSearchResponse>(`${this.baseUrl}/api/products/search`, { params });
  }

  searchProductsInBranch(
    query: string,
    branchId: number,
    limit = 20,
    lane?: 'text'
  ): Observable<ProductSearchResponse> {
    let params = new HttpParams()
      .set('q', query)
      .set('branchId', branchId)
      .set('limit', limit);
    if (lane) params = params.set('lane', lane);
    return this.http.get<ProductSearchResponse>(`${this.baseUrl}/api/products/search`, { params });
  }

  listBranchProductPrices(
    branchId: number,
    productIds: readonly number[]
  ): Observable<CatalogPricesResponse> {
    let params = new HttpParams().set('branchId', branchId);
    for (const productId of productIds) {
      params = params.append('productIds', productId);
    }
    return this.http.get<CatalogPricesResponse>(`${this.baseUrl}/api/products/prices`, { params });
  }

  createBasket(name?: string): Observable<Basket> {
    return this.http.post<Basket>(`${this.baseUrl}/api/baskets`, { name: name ?? null });
  }

  getBasket(id: number): Observable<Basket> {
    return this.http.get<Basket>(`${this.baseUrl}/api/baskets/${id}`);
  }

  upsertBasketItem(basketId: number, productId: number, quantity: number): Observable<Basket> {
    return this.http.post<Basket>(`${this.baseUrl}/api/baskets/${basketId}/items`, {
      productId,
      quantity,
      note: null
    });
  }

  removeBasketItem(basketId: number, productId: number): Observable<Basket> {
    return this.http.delete<Basket>(`${this.baseUrl}/api/baskets/${basketId}/items/${productId}`);
  }

  compareBasket(id: number, city: string): Observable<BasketComparison> {
    const params = new HttpParams().set('city', city);
    return this.http.get<BasketComparison>(`${this.baseUrl}/api/baskets/${id}/compare`, { params });
  }

  findSimilarProducts(productId: number, limit = 10): Observable<SimilarProductsResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<SimilarProductsResponse>(
      `${this.baseUrl}/api/products/${productId}/similar`,
      { params }
    );
  }

  getPriceHistory(barcode: string, city: string): Observable<PriceHistoryResponse> {
    const params = new HttpParams().set('barcode', barcode).set('city', city);
    return this.http.get<PriceHistoryResponse>(`${this.baseUrl}/api/products/price-history`, {
      params
    });
  }

  createShoppingBasket(
    name?: string,
    groupId?: number | null,
    branchId?: number | null,
    options?: { makeCurrent?: boolean; useDefaultGroup?: boolean }
  ): Observable<ShoppingBasket> {
    const body: {
      name?: string | null;
      groupId?: number | null;
      branchId?: number | null;
      makeCurrent?: boolean;
      useDefaultGroup?: boolean;
    } = {
      name: name ?? null,
      groupId: groupId ?? null,
      branchId: branchId ?? null
    };
    if (options?.makeCurrent !== undefined) body.makeCurrent = options.makeCurrent;
    if (options?.useDefaultGroup !== undefined) body.useDefaultGroup = options.useDefaultGroup;
    return this.http.post<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets`, body);
  }

  getShoppingBasket(id: number): Observable<ShoppingBasket> {
    return this.http.get<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${id}`);
  }

  setBasketBranch(basketId: number, branchId: number | null): Observable<ShoppingBasket> {
    return this.http.patch<ShoppingBasket>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/branch`,
      { branchId }
    );
  }

  getProductAlternatives(
    branchId: number,
    productId: number,
    limit = 3
  ): Observable<{ branchId: number; productId: number; alternatives: ProductAlternativeMatch[] }> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<{ branchId: number; productId: number; alternatives: ProductAlternativeMatch[] }>(
      `${this.baseUrl}/api/branches/${branchId}/products/${productId}/alternatives`,
      { params }
    );
  }

  getAlternativeCards(
    branchId: number,
    productId: number,
    limit = 5
  ): Observable<{ branchId: number; productId: number; count: number; items: ProductSearchItem[] }> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<{ branchId: number; productId: number; count: number; items: ProductSearchItem[] }>(
      `${this.baseUrl}/api/branches/${branchId}/products/${productId}/alternative-cards`,
      { params }
    );
  }

  claimShoppingBasket(basketId: number, claimToken: string | null): Observable<ShoppingBasket> {
    return this.http.post<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${basketId}/claim`, {
      claimToken
    });
  }

  renameShoppingBasket(basketId: number, name: string): Observable<ShoppingBasket> {
    return this.http.patch<ShoppingBasket>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/name`,
      { name }
    );
  }

  createBasketNeed(basketId: number, need: BasketNeedWrite): Observable<BasketNeed> {
    return this.http.post<BasketNeed>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/needs`,
      need
    );
  }

  updateBasketNeed(
    basketId: number,
    needId: number,
    need: BasketNeedWrite
  ): Observable<BasketNeed> {
    return this.http.put<BasketNeed>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/needs/${needId}`,
      need
    );
  }

  deleteBasketNeed(basketId: number, needId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/needs/${needId}`
    );
  }

  smartCompareBasket(
    basketId: number,
    request: SmartCompareRequest
  ): Observable<SmartBasketComparison> {
    return this.http.post<SmartBasketComparison>(
      `${this.baseUrl}/api/v2/baskets/${basketId}/smart-compare`,
      request
    );
  }

  listUnitReviews(
    status: UnitReviewStatus = 'pending',
    limit = 200
  ): Observable<ProductUnitReviewListResponse> {
    const params = new HttpParams().set('status', status).set('limit', limit);
    return this.http.get<ProductUnitReviewListResponse>(`${this.baseUrl}/api/ingestion/unit-reviews`, {
      params
    });
  }

  applyUnitReview(id: number): Observable<ProductUnitReview> {
    return this.http.post<ProductUnitReview>(
      `${this.baseUrl}/api/ingestion/unit-reviews/${id}/apply`,
      {}
    );
  }

  dismissUnitReview(id: number): Observable<ProductUnitReview> {
    return this.http.post<ProductUnitReview>(
      `${this.baseUrl}/api/ingestion/unit-reviews/${id}/dismiss`,
      {}
    );
  }

  getUnitReviewSourceItem(id: number): Observable<UnitReviewSourceItem> {
    return this.http.get<UnitReviewSourceItem>(
      `${this.baseUrl}/api/ingestion/unit-reviews/${id}/source-item`
    );
  }

  previewProductName(barcode: string, fetchMissing = false): Observable<NamePreviewResult> {
    let params = new HttpParams().set('barcode', barcode);
    if (fetchMissing) {
      params = params.set('fetchMissing', 'true');
    }
    return this.http.get<NamePreviewResult>(`${this.baseUrl}/api/ingestion/name-preview`, {
      params
    });
  }

  listNamePreviewSizeMismatches(
    limit = 100,
    offset = 0
  ): Observable<StorefrontSizeMismatchList> {
    const params = new HttpParams()
      .set('limit', String(limit))
      .set('offset', String(offset));
    return this.http.get<StorefrontSizeMismatchList>(
      `${this.baseUrl}/api/ingestion/name-preview/size-mismatches`,
      { params }
    );
  }

  setDisplayName(body: DisplayNameWriteRequest): Observable<{ productId: number }> {
    return this.http.post<{ productId: number }>(
      `${this.baseUrl}/api/ingestion/display-name`,
      body
    );
  }

  listClassificationCodes(category?: string): Observable<ClassificationCodesResponse> {
    let params = new HttpParams();
    if (category) {
      params = params.set('category', category);
    }
    return this.http.get<ClassificationCodesResponse>(`${this.baseUrl}/api/classification/codes`, {
      params
    });
  }

  previewClassification(
    barcode: string,
    options?: {
      includePhrase?: boolean;
      includeZeroShot?: boolean;
      includeCentroid?: boolean;
    }
  ): Observable<ClassificationPreview> {
    let params = new HttpParams().set('barcode', barcode);
    if (options?.includePhrase === false) {
      params = params.set('includePhrase', 'false');
    }
    if (options?.includeZeroShot === false) {
      params = params.set('includeZeroShot', 'false');
    }
    if (options?.includeCentroid === false) {
      params = params.set('includeCentroid', 'false');
    }
    return this.http.get<ClassificationPreview>(`${this.baseUrl}/api/classification/preview`, {
      params
    });
  }

  productImageUrl(productId: number): string {
    return `${this.baseUrl}/api/products/${productId}/image`;
  }

  getRandomClassificationProduct(): Observable<{ barcode: string }> {
    return this.http.get<{ barcode: string }>(`${this.baseUrl}/api/classification/random`);
  }

  verifyClassification(
    body: ClassificationVerifyRequest
  ): Observable<ClassificationVerifyResult> {
    return this.http.post<ClassificationVerifyResult>(
      `${this.baseUrl}/api/classification/verify`,
      body
    );
  }

  clearClassification(productId: number): Observable<{ productId: number; cleared: boolean }> {
    return this.http.post<{ productId: number; cleared: boolean }>(
      `${this.baseUrl}/api/classification/clear`,
      { productId }
    );
  }

  setTaxonomyNodeHidden(id: number, hidden: boolean): Observable<{ id: number; hidden: boolean }> {
    return this.http.post<{ id: number; hidden: boolean }>(
      `${this.baseUrl}/api/classification/nodes/${id}/hidden`,
      { hidden }
    );
  }

  createClassificationType(body: CreateClassificationTypeRequest): Observable<TypeCode> {
    return this.http.post<TypeCode>(`${this.baseUrl}/api/classification/types`, body);
  }

  runClassificationGraph(body: {
    productId?: number | null;
    barcode?: string | null;
    model?: string | null;
  }): Observable<ClassificationGraphRun> {
    return this.http.post<ClassificationGraphRun>(`${this.baseUrl}/api/classification/graph`, body);
  }

  listClassificationModels(): Observable<ClassificationModelsResponse> {
    return this.http.get<ClassificationModelsResponse>(`${this.baseUrl}/api/classification/models`);
  }

  prepareClassificationProbe(body: {
    barcode: string;
    includePhoto?: boolean;
    step?: string;
    categorySlug?: string | null;
  }): Observable<ClassificationProbePrepare> {
    return this.http.post<ClassificationProbePrepare>(
      `${this.baseUrl}/api/classification/probe/prepare`,
      body
    );
  }

  randomClassificationFlowSample(gold: boolean): Observable<ClassificationFlowSample> {
    return this.http.get<ClassificationFlowSample>(
      `${this.baseUrl}/api/classification/flow/sample`,
      { params: { gold } }
    );
  }

  randomGoldClassificationProbe(): Observable<ClassificationProbeGoldSample> {
    return this.http.get<ClassificationProbeGoldSample>(
      `${this.baseUrl}/api/classification/probe/random-gold`
    );
  }

  runClassificationProbe(body: {
    barcode: string;
    system: string;
    user: string;
    model?: string | null;
    includePhoto?: boolean;
  }): Observable<ClassificationProbeRun> {
    return this.http.post<ClassificationProbeRun>(
      `${this.baseUrl}/api/classification/probe/run`,
      body
    );
  }

  listClassificationProposals(): Observable<{ items: ClassificationProposal[] }> {
    return this.http.get<{ items: ClassificationProposal[] }>(
      `${this.baseUrl}/api/classification/proposals`
    );
  }

  listClassificationProposalProducts(
    id: number
  ): Observable<{ items: ClassificationProposalProduct[] }> {
    return this.http.get<{ items: ClassificationProposalProduct[] }>(
      `${this.baseUrl}/api/classification/proposals/${id}/products`
    );
  }

  acceptClassificationProposal(id: number): Observable<{ id: number; accepted: boolean }> {
    return this.http.post<{ id: number; accepted: boolean }>(
      `${this.baseUrl}/api/classification/proposals/${id}/accept`,
      {}
    );
  }

  rejectClassificationProposal(
    id: number,
    productId?: number | null
  ): Observable<{ id: number; rejected: boolean; productId?: number | null }> {
    return this.http.post<{ id: number; rejected: boolean; productId?: number | null }>(
      `${this.baseUrl}/api/classification/proposals/${id}/reject`,
      productId != null ? { productId } : {}
    );
  }

  listAutosavedClassifications(
    model?: string
  ): Observable<{ items: ClassificationAutosavedItem[] }> {
    let params = new HttpParams();
    if (model) {
      params = params.set('model', model);
    }
    return this.http.get<{ items: ClassificationAutosavedItem[] }>(
      `${this.baseUrl}/api/classification/autosaved`,
      { params }
    );
  }

  getWorkersStatus(): Observable<WorkersStatusResponse> {
    return this.http.get<WorkersStatusResponse>(`${this.baseUrl}/api/workers`);
  }

  getWorkersFeed(sinceId = 0, sinceEventId = 0, limit = 80): Observable<WorkersFeedResponse> {
    const params = new HttpParams()
      .set('sinceId', sinceId)
      .set('sinceEventId', sinceEventId)
      .set('limit', limit);
    return this.http.get<WorkersFeedResponse>(`${this.baseUrl}/api/workers/feed`, { params });
  }

  getWorkersIssues(): Observable<WorkersIssuesResponse> {
    return this.http.get<WorkersIssuesResponse>(`${this.baseUrl}/api/workers/issues`);
  }

  listSiteVisits(limit = 300): Observable<SiteVisitListResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http.get<SiteVisitListResponse>(`${this.baseUrl}/api/site-visits`, { params });
  }

  runWorkerAction(action: WorkerSuggestedAction): Observable<unknown> {
    const url = `${this.baseUrl}${action.path}`;
    if (action.method.toUpperCase() === 'POST') {
      return this.http.post(url, {});
    }
    return this.http.request(action.method, url);
  }

  /**
   * Classification flow (SSE). Writes a label only when the product is not admin-locked
   * and two companies agree. Gold products are compared and not written.
   */
  async *streamClassificationFlow(
    barcode: string,
    options?: { includePhoto?: boolean; signal?: AbortSignal }
  ): AsyncGenerator<ClassificationFlowEvent, void, unknown> {
    yield* this.readClassificationSse(
      `${this.baseUrl}/api/classification/flow/stream`,
      { barcode, includePhoto: options?.includePhoto ?? false },
      options?.signal
    );
  }

  /** Shadow scoreboard over admin-verified products (SSE). */
  async *streamClassificationScoreboard(
    limit = 20,
    options?: { includePhoto?: boolean; model?: string | null; signal?: AbortSignal }
  ): AsyncGenerator<ClassificationFlowEvent, void, unknown> {
    const model = (options?.model || '').trim();
    yield* this.readClassificationSse(
      `${this.baseUrl}/api/classification/flow/scoreboard`,
      {
        limit,
        includePhoto: model ? false : (options?.includePhoto ?? false),
        ...(model ? { model } : {})
      },
      options?.signal
    );
  }

  createClassificationFlowBatch(
    limit: number,
    batchApi = false
  ): Observable<ClassificationFlowBatchDetail> {
    return this.http.post<ClassificationFlowBatchDetail>(
      `${this.baseUrl}/api/classification/flow/batches`,
      { limit, batchApi }
    );
  }

  listClassificationFlowBatches(): Observable<ClassificationFlowBatchSummary[]> {
    return this.http.get<ClassificationFlowBatchSummary[]>(
      `${this.baseUrl}/api/classification/flow/batches`
    );
  }

  getClassificationFlowBatch(id: number): Observable<ClassificationFlowBatchDetail> {
    return this.http.get<ClassificationFlowBatchDetail>(
      `${this.baseUrl}/api/classification/flow/batches/${id}`
    );
  }

  getClassificationFlowBatchItem(
    batchId: number,
    productId: number
  ): Observable<ClassificationFlowBatchItemDetail> {
    return this.http.get<ClassificationFlowBatchItemDetail>(
      `${this.baseUrl}/api/classification/flow/batches/${batchId}/items/${productId}`
    );
  }

  getClassificationFlowBatchCall(
    batchId: number,
    customId: string
  ): Observable<ClassificationFlowBatchCallDetail> {
    return this.http.get<ClassificationFlowBatchCallDetail>(
      `${this.baseUrl}/api/classification/flow/batches/${batchId}/calls/${encodeURIComponent(customId)}`
    );
  }

  approveClassificationFlowVote(
    batchId: number,
    productId: number,
    model: string
  ): Observable<ClassificationFlowVoteApproval> {
    return this.http.post<ClassificationFlowVoteApproval>(
      `${this.baseUrl}/api/classification/flow/batches/${batchId}/items/${productId}/approve`,
      { model }
    );
  }

  rerunClassificationFlowBatchItem(
    batchId: number,
    productId: number
  ): Observable<ClassificationFlowBatchItemDetail> {
    return this.http.post<ClassificationFlowBatchItemDetail>(
      `${this.baseUrl}/api/classification/flow/batches/${batchId}/items/${productId}/rerun`,
      {}
    );
  }

  private async *readClassificationSse(
    url: string,
    body: object,
    signal?: AbortSignal
  ): AsyncGenerator<ClassificationFlowEvent, void, unknown> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify(body),
      signal
    });

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const errBody = (await response.json()) as { error?: string };
        if (errBody?.error) {
          detail = errBody.error;
        }
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }

    if (!response.body) {
      throw new Error('No response body for SSE stream.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const evt = this.parseSseChunk(chunk);
        if (evt) {
          yield evt;
        }
      }
    }

    if (buffer.trim()) {
      const evt = this.parseSseChunk(buffer);
      if (evt) {
        yield evt;
      }
    }
  }

  private parseSseChunk(chunk: string): ClassificationFlowEvent | null {
    const lines = chunk.split('\n');
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length === 0) {
      return null;
    }
    try {
      return JSON.parse(dataLines.join('\n')) as ClassificationFlowEvent;
    } catch {
      return null;
    }
  }
}
